import { Platform } from "react-native"

import * as Application from "expo-application"
import Constants from "expo-constants"

/**
 * Returns the app version string for display and API use.
 *
 * In local dev (Metro/dev client), the committed native project carries a stale
 * versionName, so we prefer the app.json version (via Constants.expoConfig) to
 * avoid showing an outdated number. In release/EAS builds the native value is
 * authoritative — EAS manages the version remotely (see eas.json
 * `appVersionSource: "remote"`) — so we prefer it. On web there is no native
 * value, so expoConfig is used in all cases.
 */
export function getAppVersion(): string | null {
  if (__DEV__) {
    return Constants.expoConfig?.version ?? Application.nativeApplicationVersion ?? null
  }
  return Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? null
}

/**
 * Returns the native build number (iOS build / Android versionCode) as a string,
 * or null. Only meaningful on native; returns null on web (which has no build
 * number — it surfaces the git sha instead). Intended for display in non-prod.
 *
 * Returns null in local dev because the real build number is assigned by EAS at
 * build time (eas.json `appVersionSource: "remote"`); a Metro/dev-client build
 * only carries the committed placeholder (e.g. "1"), which would be misleading.
 */
export function getNativeBuildNumber(): string | null {
  if (Platform.OS === "web" || __DEV__) return null
  return Application.nativeBuildVersion ?? null
}

/**
 * Returns the short git sha the current web build was produced from, or null.
 *
 * Only meaningful on web: it is stamped into the bundle by
 * `scripts/generate-web-version.js` during `yarn bundle:web`. Returns null on
 * native (mobile builds surface the build number instead) and in local web dev
 * (where no manifest is generated). Intended for display in non-prod only.
 */
export function getWebGitSha(): string | null {
  if (Platform.OS !== "web") return null
  const sha = Constants.expoConfig?.extra?.webGitSha
  if (typeof sha !== "string" || !sha || sha === "nogit") return null
  return sha
}
